import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema
}: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);

      updateMetaTag('og:url', canonical, true);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add Schema.org JSON-LD
    if (schema) {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup schema on unmount
      if (schema) {
        const script = document.querySelector('script[type="application/ld+json"]');
        if (script) {
          script.remove();
        }
      }
    };
  }, [title, description, keywords, canonical, ogType, ogImage, schema]);
};

export const generateLocalBusinessSchema = () => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#organization`,
    name: 'CleanHaus',
    image: `${siteUrl}/logo.png`,
    description: 'Professional pressure washing services in Henderson and Las Vegas Metro area. Expert cleaning for residential and commercial properties.',
    url: siteUrl,
    telephone: '+1-702-332-5893',
    email: 'CleanHauspw@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Main Street',
      addressLocality: 'Henderson',
      addressRegion: 'NV',
      postalCode: '89002',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 36.0395,
      longitude: -114.9817
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Henderson',
        '@id': 'https://en.wikipedia.org/wiki/Henderson,_Nevada'
      },
      {
        '@type': 'City',
        name: 'Las Vegas',
        '@id': 'https://en.wikipedia.org/wiki/Las_Vegas'
      }
    ],
    priceRange: '$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Sunday', 'Monday'],
        opens: '07:00',
        closes: '19:00'
      }
    ],
    sameAs: [
      'https://www.facebook.com/cleanhaus',
      'https://www.instagram.com/cleanhaus',
      'https://www.linkedin.com/company/cleanhaus'
    ]
  };
};

export const generateServiceSchema = () => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Pressure Washing',
    provider: {
      '@type': 'LocalBusiness',
      name: 'CleanHaus',
      '@id': `${siteUrl}/#organization`
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Henderson'
      },
      {
        '@type': 'City',
        name: 'Las Vegas'
      }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Pressure Washing Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Driveway Pressure Washing',
            description: 'Professional driveway cleaning and restoration'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Roof Cleaning',
            description: 'Safe and effective roof cleaning services'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Patio & Deck Cleaning',
            description: 'Restore your outdoor living spaces'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'House Washing',
            description: 'Complete exterior house cleaning'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Commercial Pressure Washing',
            description: 'Professional cleaning for businesses'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Gutter Cleaning',
            description: 'Thorough gutter cleaning and maintenance'
          }
        }
      ]
    }
  };
};

export const generateWebPageSchema = (pageName: string, description: string) => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageName,
    description: description,
    url: siteUrl,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: 'CleanHaus',
      url: siteUrl
    },
    about: {
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`
    }
  };
};

export const generateAggregateRatingSchema = () => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#organization`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '127',
      bestRating: '5',
      worstRating: '1'
    }
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};
